import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Play, Mic } from 'lucide-react';
import MediaLightbox from './MediaLightbox';

function MediaGalleryHero({ media = [], currentIndex = 0, onIndexChange }) {
  // Check if media type is audio (either 'audio' type or video with audio filename)
  const isAudioMedia = (item) => {
    return item.type === 'audio' ||
      (item.type === 'video' && item.url?.includes('audio'));
  };
  const [showModal, setShowModal] = useState(false);
  const [modalIndex, setModalIndex] = useState(0);

  if (media.length === 0) return null;

  const currentMedia = media[currentIndex];

  const handlePrev = () => {
    const newIndex = currentIndex === 0 ? media.length - 1 : currentIndex - 1;
    onIndexChange?.(newIndex);
  };

  const handleNext = () => {
    const newIndex = currentIndex === media.length - 1 ? 0 : currentIndex + 1;
    onIndexChange?.(newIndex);
  };

  const handleMainClick = () => {
    setModalIndex(currentIndex);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  return (
    <div className="media-gallery-hero-wrapper">
      {/* Hero Gallery */}
      <div className="media-gallery-hero" onClick={handleMainClick}>
        {isAudioMedia(currentMedia) ? (
          <div className="hero-audio">
            <div className="audio-player-card">
              <div className="audio-icon">
                <Mic size={48} />
              </div>
              <p className="audio-label">Voice Recording</p>
              <p className="audio-hint">Click to play</p>
            </div>
          </div>
        ) : currentMedia.type === 'video' ? (
          <div className="hero-video">
            <video src={`${currentMedia.url}#t=0.5`} muted playsInline preload="auto" />
            <div className="video-play-overlay hero-video-overlay">
              <div className="video-play-button hero-play-button">
                <Play size={32} fill="#333" />
              </div>
            </div>
          </div>
        ) : (
          <img
            src={currentMedia.url}
            alt={`Media ${currentIndex + 1}`}
            onError={(e) => {
              e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23e0e0e0" width="100" height="100"/><text x="50" y="55" text-anchor="middle" fill="%23999" font-size="12">No Image</text></svg>';
            }}
          />
        )}

        {/* Photo count badge */}
        <div className="photo-count-badge hero-count-badge">
          {currentIndex + 1} / {media.length}
        </div>

        {/* Navigation Arrows */}
        {media.length > 1 && (
          <>
            <button
              className="gallery-nav-arrow gallery-nav-arrow-left"
              onClick={(e) => {
                e.stopPropagation();
                handlePrev();
              }}
            >
              <ChevronLeft size={20} />
            </button>
            <button
              className="gallery-nav-arrow gallery-nav-arrow-right"
              onClick={(e) => {
                e.stopPropagation();
                handleNext();
              }}
            >
              <ChevronRight size={20} />
            </button>
          </>
        )}

        {/* Carousel Dots */}
        {media.length > 1 && (
          <div className="gallery-dots">
            {media.map((_, index) => (
              <button
                key={index}
                className={`gallery-dot ${index === currentIndex ? 'gallery-dot-active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onIndexChange?.(index);
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Thumbnail Strip */}
      <div className="thumbnail-strip">
        {media.map((item, index) => (
          <div
            key={item.id || index}
            className={`thumbnail-strip-item ${index === currentIndex ? 'thumbnail-strip-item-active' : ''}`}
            onClick={() => onIndexChange?.(index)}
          >
            {isAudioMedia(item) ? (
              <div className="thumb-audio">
                <Mic size={16} />
              </div>
            ) : item.type === 'video' ? (
              <div className="thumb-video">
                <video src={`${item.url}#t=0.5`} muted playsInline preload="auto" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <div className="thumb-video-overlay small">
                  <Play size={12} fill="#fff" />
                </div>
              </div>
            ) : (
              <img
                src={item.thumbnailUrl || item.url}
                alt={`Thumbnail ${index + 1}`}
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23e0e0e0" width="100" height="100"/></svg>';
                }}
              />
            )}
          </div>
        ))}
      </div>

      {/* Full-screen Lightbox (TimeCapsule style) */}
      {showModal && (
        <MediaLightbox
          media={media}
          currentIndex={modalIndex}
          onIndexChange={setModalIndex}
          onClose={closeModal}
        />
      )}
    </div>
  );
}

export default MediaGalleryHero;
